// tslint:disable: no-console
import { CodeCell } from "@jupyterlab/cells";
import { CodeMirrorEditor } from "@jupyterlab/codemirror";
import { setInnerHtmlSanitized, wait } from "@kaggle/core";
import { JupyterLabGlobal } from "@kaggle/jupyterlab";
import { Icon } from "@kaggle/material";
import { Kaggle, featureFlags, reportEvent } from "@kaggle/sdk";
import {
  defaultTheme,
  RMWCProvider,
  ThemeProvider,
} from "@kaggle/styled-components";
import * as React from "react";
import { render } from "react-dom";
import { AnyAction } from "redux";
import { v4 as uuid } from "uuid";
import { CircularProgressButton } from "../../connector/extensions/components/buttons/CircularProgressButton";
import { ExecuteIconButton } from "../../connector/extensions/components/buttons/ExecuteIconButton";
import { ExecuteSelectionIconButton } from "../../connector/extensions/components/buttons/ExecuteSelectionIconButton";
import { CellSpacer } from "../../connector/extensions/components/CellSpacer";
import { ScriptTips } from "../../connector/extensions/components/ScriptTips";
import {
  CellExecution,
  executionTooltip,
} from "../../connector/extensions/types/CellExecution";
import CellProps from "../../connector/extensions/types/CellProps";
import { JupyterLabNotebookClient } from "../../jupyter/services/createJupyterLabClient";
import { JupyterServices } from "../../jupyter/types";
import { getSessionState } from "../../redux/selectors/session";
import { getThemeName } from "../../redux/selectors/theme";
import { FrameStore } from "../../redux/types";
import { getPythonScriptTips } from "../../scripttips/PythonScriptTips";
import { CellType } from "../../types/CellType";
import { animationFrameDelay } from "./utils/animationFrameDelay";
import { pubSub } from "./utils/pubSub";

import EditorType = Kaggle.Sdk.Kernels.EditorType;

/** COPIED FROM https://github.com/jupyterlab/jupyterlab/blob/b4db7f03d2ad0b91a9c8c252c56ae5e4a9408fbb/packages/cells/src/placeholder.tsx#L93 */
const INPUT_PROMPT_CLASS = "jp-Placeholder-prompt jp-InputPrompt";
const OUTPUT_PROMPT_CLASS = "jp-Placeholder-prompt jp-OutputPrompt";
const CONTENT_CLASS = "jp-Placeholder-content";

/** A special attribute we add to cells to identify them in cell props map. */
const CELL_UUID_ATTRIBUTE_NAME = "data-uuid";

/** A special attribute we add to cells to track initialization. */
const CELL_BOOTSTRAP_ATTRIBUTE_NAME = "data-bootstrap";

const OUTPUT_ERROR_DATA_KEY = "application/vnd.jupyter.error";
const ERROR_MESSAGE_KEY = "evalue";

/** Possible values of the data-bootstrap attribute. */
enum CellBootstrapStatus {
  NONE,
  INIT = "init",
  DONE = "done",
}

/* JupyterLab uses "[*]:" as the cell prompt while the kernel is running. */
const RUNNING_CELL_PROMPT_TEXT = "[*]:";

/** Organizes communications from the notebook DOM to its shadow DOM components. */
interface ShadowClient {
  /** Schedule cell shadow DOM component injection after N elapsed frames. */
  scheduleInjectCells: (waitFrames?: number) => void;
  /** Schedule cell shadow DOM component updates after N elapsed frames. */
  scheduleUpdateCells: (waitFrames?: number) => void;
}

/** Gets the Notebook DOM node (direct parent of all the cells). */
const getNotebookDOM = () => document.getElementsByClassName("jp-Notebook")[0];

// This is necessary to inject material css.
const loadCss = (id: string, href: string) => {
  let link = document.getElementById(id) as HTMLLinkElement;
  if (link) {
    return link;
  }

  const head = document.getElementsByTagName("head")[0];
  link = document.createElement("link");
  link.id = id;
  link.rel = "stylesheet";
  link.type = "text/css";
  link.href = href;
  link.media = "all";
  head.appendChild(link);
  return link;
};

const SHADOW_DOM_STYLE_ID = "kaggle-shadow-style";
/** This style is injected into head and linked to shadow DOMs for styling buttons. */
const loadShadowStyle = () => {
  let style = document.getElementById(SHADOW_DOM_STYLE_ID);
  if (style) {
    console.info("loadShadowStyle:EXISTS");
    return style;
  }
  console.info("loadShadowStyle:DNE");

  const head = document.getElementsByTagName("head")[0];
  style = document.createElement("style");
  style.id = SHADOW_DOM_STYLE_ID;
  style.textContent = `
    button {
      border-radius: 18px;
      cursor: pointer;
      font-family: Inter;
      font-style: normal;
      font-weight: 500;
      font-size: 14px;
      line-height: 20px;
      margin: 4px;
      height: 28px;
      padding: 0 16px;
      transition: all 0.3s ease;
      width: fit-content;
    }

    button > i {
      font-weight: 700 !important;
      margin-right: 3px;
      margin-left: -3px;
    }

    button:first-child {
      margin-left: 0;
    }

    button:hover {
    }

    button:focus {
      outline: none;
    }

    .light .script-tips-main-div {
      border-color: #DADCE0;
      color: #3C4043;
    }

    .light .script-tips-main-div a {
      color: #202124;
      border-bottom: 1px solid #9AA0A6;
      cursor: pointer;
      text-decoration: none;  
    }

    .light .script-tips-main-div a:visited {
      border-color: #3C4043;
    }

    .light .script-tips-main-div a:hover {
      border-color: #202124;
    }

    .dark .script-tips-main-div {
      border-color: #5F6368;
      color: #fff;
    }

    .dark .script-tips-main-div a {
      color: #EAEAED;
      border-bottom: 1px solid;
      border-color: #9AA0A6;
      cursor: pointer;
      text-decoration: none;
    }

    .dark .script-tips-main-div a:visited {
      border-color: #9AA0A6;
    }
    
    .dark .script-tips-main-div a:hover {
      border-color: #EAEAED;
    }

    .light button {
      background: transparent;
      color: #202124;
      border: 1px solid #BDC1C6;
    }

    .light button:hover {
      border: 1px solid #202124;
    }

    .dark button {
      background: transparent;
      color: rgba(255,255,255,0.8);
      border: 1px solid #BDC1C6;
    }

    .dark button:hover {
      border: 1px solid #9AA0A6;
      background-color: rgba(0,0,0,0.5);
    }

    /* Circular progress */
    .light .circular-progress-stop-icon {
      background-color: #3C4043 !important;
    }

    .dark .circular-progress-stop-icon {
      background-color: #fff !important;
    }

    .rmwc-circular-progress--indeterminate .rmwc-circular-progress__circle {
      animation: rmwc-circular-progress-indeterminate-bar-rotate 2s linear infinite;
    }
  
    .rmwc-circular-progress__circle {
      height: 100%;
      width: 100%;
    }

    .rmwc-icon .rmwc-circular-progress {
      font-size: inherit;
    }

    .rmwc-circular-progress--indeterminate .rmwc-circular-progress__path {
      animation: rmwc-circular-progress-indeterminate-bar-dash 1.5s ease-in-out infinite;
      stroke-dasharray: 2.08%, 416%;
      stroke-dashoffset: 0;
    }

    .rmwc-circular-progress__path {
      -webkit-transition: stroke-dasharray 0.35s cubic-bezier(0.4, 0, 0.2, 1);
      transition: stroke-dasharray 0.35s cubic-bezier(0.4, 0, 0.2, 1);
      fill: none;
      stroke-dasharray: 0, 416%;
      stroke-dashoffset: 0;
      stroke-linecap: round;
      stroke-miterlimit: 20;
      stroke-width: 2.5px;
      stroke: #20BEFF;
    }

    /* Controls visibility / color of cell button circular background on hover (light). */
    .light .cell-execution-button:hover .circular-hover {
      fill: #f1f3f4;
    }
  
    /* Controls visibility / color of cell button circular background on hover (dark). */
    .dark .cell-execution-button:hover .circular-hover {
      fill: #202124;
    }

    .light .circular-progress-background .rmwc-circular-progress__path {
      stroke: #E8EAED;
    }

    .dark .circular-progress-background .rmwc-circular-progress__path {
      stroke: #5F6368;
    }

    @keyframes rmwc-circular-progress-indeterminate-bar-rotate {
      100% {
        transform: rotate(360deg);
      }
    }

    @keyframes rmwc-circular-progress-indeterminate-bar-dash {
      0% {
        stroke-dasharray: 2.08%, 416%;
        stroke-dashoffset: 0%;
      }
      50% {
        stroke-dasharray: 185.4%, 416%;
        stroke-dashoffset: -72.9%;
      }
      to {
        stroke-dasharray: 185.4%, 416%;
        stroke-dashoffset: -258.33%;
      }
    }
  `;
  head.appendChild(style);
  return style;
};

type CellRenderer = (cellProps: CellProps) => JSX.Element;

interface Props {
  store: FrameStore<AnyAction>;
  cellProps: CellProps;
  children: CellRenderer;
}

// This component is a proof of concept, it will be heavily refactored shortly.
const CellState = (props: Props): JSX.Element => {
  const state = props.store.getState();
  const [cellProps, setCellProps] = React.useState(props.cellProps);
  const [themeName, setThemeName] = React.useState(getThemeName(state));
  React.useEffect(() => {
    // This usage of DOM MutationObserver is a low overhead way to
    // synchronize component theme to the active JupyterLab theme.
    // It is low cost since we are only watching the attributes
    // list of document.body.
    const mutationObserver = new MutationObserver(() => {
      const jlThemeName = document.body.getAttribute("data-jp-theme-name");
      setThemeName(jlThemeName === "JupyterLab Dark" ? "dark" : "light");
    });
    mutationObserver.observe(document.body, {
      attributes: true,
      childList: false,
      subtree: false,
    });

    const unsubscribes = [
      pubSub.subscribe(cellProps.id, (updatedProps: CellProps) => {
        setCellProps(updatedProps);
      }).unsubscribe,
      mutationObserver.disconnect,
    ];

    // Return disposer to unsubscribe.
    return () => {
      unsubscribes.forEach(unsubscribe => {
        unsubscribe();
      });
    };
  }, []);
  return <div className={themeName}>{props.children(cellProps)}</div>;
};

const cellPropsMap: Map<string, CellProps> = new Map();

interface EventHandlers {
  onInsertCell: () => void;
}

export const renderShadowDOM = async (
  notebookClient: JupyterLabNotebookClient,
  store: FrameStore,
  jl: JupyterLabGlobal,
  handlers: EventHandlers
) => {
  // This is an expected prop of the legacy Jupyter React components.
  // Will update to only use the notebookClient implementation in future controls.
  const services: JupyterServices = {
    notebookClient,
  } as any;

  const getSourceType = () => {
    const sessionState = getSessionState(store.getState());
    return sessionState.sourceType;
  };

  // Used to clone material css into shadow DOM.
  const materialLink = loadCss(
    "material_icons_css",
    "https://fonts.googleapis.com/icon?family=Google+Material+Icons"
  );

  let shadowStyle: HTMLElement;

  // Used to clone button styles etc. into shadow DOM.
  while (true) {
    shadowStyle = loadShadowStyle();
    if (shadowStyle) {
      break;
    }
    await wait(1000);
  }

  /**
   * Calculates each cells state and publishes a message for each cell related component
   * with atomic state of the cell (combination of JupyterLab cell state and additional
   * state necessary for Kaggle UI).
   *
   * Should be invoked after existing cell state updates occur (post-DOM update from JL).
   */
  const updateCellsShadowDOM = () => {
    const widget = jl.notebookTracker.currentWidget;
    if (!widget) {
      console.warn(
        "notebookTracker.currentWidget DNE. Increase timing window."
      );
      return;
    }
    const notebookDOM = getNotebookDOM();

    const rows = Array.from(notebookDOM.children) as HTMLDivElement[];

    // TODO(cochamberlain): Refactor to use JL API.
    // We only want the cells.
    const cells = rows.filter(row => row.matches(".jp-Cell"));

    // This allows us to flag when race is detected and re-bootstrap.
    let raceDetected = false;

    cells.forEach((cell, index) => {
      if (!cell.hasAttribute(CELL_UUID_ATTRIBUTE_NAME)) {
        cell.setAttribute(CELL_UUID_ATTRIBUTE_NAME, uuid());
      }

      // Scripts don't get custom buttons so stop here.
      if (getSourceType() === EditorType.SCRIPT) {
        return;
      }

      const id = cell.getAttribute(CELL_UUID_ATTRIBUTE_NAME);
      const isActive = cell.className.includes("jp-mod-active");
      const cellType: CellType = cell.className.includes("jp-CodeCell")
        ? "code"
        : "markdown";

      // Detect whether cell / output collapsed via existence of ellipsis in DOM.
      const cellWidget = widget.content.widgets[index];
      const promptContent = cellWidget.promptNode.textContent ?? "";
      const promptText = notebookClient.isQueued(cell.id)
        ? RUNNING_CELL_PROMPT_TEXT
        : // BUG(b/210694551): There seems to be a weird race with promptContent
        // still showing RUNNING_CELL_PROMPT_TEXT when the kernel is actually idle
        // in order to avoid showing a spinner when we are idle, we'll make sure to
        // only copy the prompt when we are actually busy.
        jl.context?.sessionContext?.kernelDisplayStatus === "busy"
        ? promptContent
        : "";
      const isCollapsed = cellWidget.inputHidden;
      let isOutputCollapsed = false;
      const model = cellWidget?.model;
      if (model?.type === "code") {
        const codeCell = cellWidget as CodeCell;
        isOutputCollapsed = codeCell.outputHidden;
        if (featureFlags.kernelEditorScriptTips) {
          codeCell.outputArea.model.changed.connect((_, change) => {
            if (!id) {
              // this shouldn't happen, but there's nothing we can do...
              return;
            }

            let cellPropsValue = cellPropsMap.get(id);
            if (!cellPropsValue) {
              // this shouldn't happen, but there's nothing we can do...
              return;
            }

            let newTip;
            if (
              change.type === "add" &&
              change.newValues.length > 0 &&
              change.newValues[0].type === "error" &&
              change.newValues[0].data[OUTPUT_ERROR_DATA_KEY]
            ) {
              const error = change.newValues[0].data[OUTPUT_ERROR_DATA_KEY];
              const message = (error as any)[ERROR_MESSAGE_KEY];
              newTip = getPythonScriptTips(message) || undefined;
              if (
                newTip?.scriptTipCode &&
                newTip.scriptTipCode !== cellPropsValue.scriptTip?.scriptTipCode
              ) {
                reportEvent("kernels", "show-script-tip", {
                  code: newTip.scriptTipCode,
                });
              }
            }

            cellPropsValue = {
              ...cellPropsValue,
              scriptTip: newTip,
            };
            cellPropsMap.set(id, cellPropsValue);
            pubSub.publish(id, cellPropsValue);
          });
        }
      }
      const selection =
        ((cellWidget.editor as CodeMirrorEditor).doc.getSelection() as
          | string
          | null) ?? "";
      const linesSelected = selection !== "";

      if (!id) {
        console.warn(`Cell at index ${index} had no id (data-uuid).`);
        return;
      }

      let cellProps = cellPropsMap.get(id);

      if (cellProps) {
        cellProps = {
          ...cellProps,
          index,
          isActive,
          promptText,
          cellType,
          isCollapsed,
          execution: model?.metadata.get("execution") as any as CellExecution,
          isOutputCollapsed,
          onToggleCollapse: () => {
            cellWidget.inputHidden = !cellWidget.inputHidden;
          },
          linesSelected,
        };
      } else {
        // If we have no cellProps for this id, it was not properly bootstrapped (race).
        raceDetected = true;
        console.warn(`Cell has not been bootstrapped: ${id}.`);
        return;
      }

      cellPropsMap.set(id, cellProps);
      pubSub.publish(id, cellProps);
    });
    // When race is detected, re-run the cell injection bootstrap and re-update.
    if (raceDetected) {
      console.warn("RACE DETECTED:INJECTING CELLS");
      window.setImmediate(() => {
        injectCellsShadowDOM();
        updateCellsShadowDOM();
      });
    }
  };

  /** Get all the cell DOM elements in the notebook and inject shadow roots. */
  const injectCellsShadowDOM = () => {
    const widget = jl.notebookTracker.currentWidget;
    if (!widget) {
      console.warn(
        "notebookTracker.currentWidget DNE. Increase timing window."
      );
      return;
    }

    const notebookDOM = getNotebookDOM();

    const rows = Array.from(notebookDOM.children) as HTMLDivElement[];

    // We only want the cells. There are custom spacers in-between.
    const cells = rows.filter(row => row.matches(".jp-Cell"));

    cells.forEach((cell, index) => {
      // Get the current bootstrap status or default to NONE.
      const bootstrapStatus =
        (cell.getAttribute(
          CELL_BOOTSTRAP_ATTRIBUTE_NAME
        ) as CellBootstrapStatus) || CellBootstrapStatus.NONE;

      switch (bootstrapStatus) {
        // Only bootstrap cells one time. Exit early on subsequent runs.
        case CellBootstrapStatus.DONE:
          return;
        // If the status is INIT, it means the prior bootstrap exited early or errored.
        case CellBootstrapStatus.INIT:
          console.warn(
            "Prior cell bootstrap initialized but did not complete successfully."
          );
          break;
      }

      // Update the bootstrap status to INIT. This lets us know this process was started.
      cell.setAttribute(
        CELL_BOOTSTRAP_ATTRIBUTE_NAME,
        CellBootstrapStatus.INIT
      );

      let id: string = cell.getAttribute(CELL_UUID_ATTRIBUTE_NAME)!;
      if (!id) {
        id = uuid();
        cell.setAttribute(CELL_UUID_ATTRIBUTE_NAME, id);
      }

      // Scripts don't get custom buttons so stop here.
      if (getSourceType() === EditorType.SCRIPT) {
        return;
      }

      const isActive = cell.className.includes("jp-mod-active");
      const cellType: CellType = cell.className.includes("jp-CodeCell")
        ? "code"
        : "markdown";

      // Detect whether cell / output collapsed via existence of ellipsis in DOM.
      const cellWidget = widget.content.widgets[index];
      const isCollapsed = cellWidget.inputHidden;
      let isOutputCollapsed = false;
      if (cellWidget?.model?.type === "code") {
        const codeCell = cellWidget as CodeCell;
        isOutputCollapsed = codeCell.outputHidden;
      }

      const inputPlaceholder = (cellWidget as any)._inputPlaceholder;
      inputPlaceholder.render = () => {
        return [
          <div className={INPUT_PROMPT_CLASS} key="input" />,
          <div
            role="button"
            className={CONTENT_CLASS}
            onClick={e => inputPlaceholder.handleClick(e)}
            key="content"
          >
            Expand
          </div>,
        ];
      };
      if (isCollapsed) {
        cellWidget.inputHidden = false;
        cellWidget.inputHidden = true; // Trigger re-render.
      }

      const outputPlaceholder = (cellWidget as any)._outputPlaceholder;
      if (outputPlaceholder) {
        outputPlaceholder.render = () => {
          return [
            <div className={OUTPUT_PROMPT_CLASS} key="output" />,
            <div
              role="button"
              className={CONTENT_CLASS}
              onClick={e => outputPlaceholder.handleClick(e)}
              key="content"
            >
              Expand
            </div>,
          ];
        };
        if (isOutputCollapsed) {
          const codeCell = cellWidget as CodeCell;
          codeCell.outputHidden = false;
          codeCell.outputHidden = true; // Trigger re-render.
        }
      }

      const selection =
        ((cellWidget.editor as CodeMirrorEditor).doc.getSelection() as
          | string
          | null) ?? "";
      const linesSelected = selection !== "";

      if (!id) {
        console.warn(`Cell at index ${index} had no id (data-uuid).`);
        return;
      }

      const cellProps: CellProps = {
        id,
        index,
        cellType,
        isActive,
        isDragging: false,
        isHovering: false,
        isCollapsed,
        isOutputCollapsed,
        // TODO(cochamberlain): Determine isCodeHidden state.
        isCodeHidden: false,
        // TODO(cochamberlain): Determine isOutputHidden state.
        isOutputHidden: false,
        // TODO(cochamberlain): Determine isFrozen state.
        isFrozen: false,
        onToggleCodeHidden: () => {
          // TODO(cochamberlain): Toggle code hidden state. Don't see this on notebookClient.
        },
        onToggleOutputHidden: () => {
          // TODO(cochamberlain): Toggle output hidden state. Don't see this on notebookClient.
        },
        onToggleCollapse: () => {
          cellWidget.inputHidden = cellWidget.inputHidden;
        },
        onToggleOutputCollapse: () => {
          if (cellProps.isOutputCollapsed) {
            notebookClient.expandOutput(index);
          } else {
            notebookClient.collapseOutput(index);
          }
        },
        onSetFrozenState: (isFrozen: boolean) => {
          notebookClient.setCellFrozenState(index, isFrozen);
        },
        linesSelected,
        // TODO(cochamberlain): Pass through actual sourceType (support scripts).
        sourceType: Kaggle.Sdk.Kernels.EditorType.NOTEBOOK,
        // TODO(cochamberlain): Pass through themeName.
        themeName: "dark",
        // TODO(cochamberlain): Pass through isAdmin.
        isAdmin: false,
        services,
      };

      cellPropsMap.set(id, cellProps);

      const handleMouseOver = () => {
        cell.removeEventListener("mouseover", handleMouseOver);
        let props = cellPropsMap.get(id);
        if (!props) {
          console.warn(
            `cellPropsMap did not contain an item for cell id ${id}. (mouseover)`
          );
          return;
        }
        props = {
          ...props,
          isHovering: true,
        };
        cellPropsMap.set(id, props);
        pubSub.publish(id, props);

        const handleMouseOut = () => {
          cell.removeEventListener("mouseout", handleMouseOut);
          let cellPropsValue = cellPropsMap.get(id);
          if (!cellPropsValue) {
            console.warn(
              `cellPropsMap did not contain an item for cell id ${id}. (mouseout)`
            );
            return;
          }
          cellPropsValue = {
            ...cellPropsValue,
            isHovering: false,
          };
          cellPropsMap.set(id, cellPropsValue);
          pubSub.publish(id, cellPropsValue);

          // Start listening again for mouse over.
          cell.addEventListener("mouseover", handleMouseOver);
        };
        cell.addEventListener("mouseout", handleMouseOut);
      };
      cell.addEventListener("mouseover", handleMouseOver);

      const overrideDOMNode = (
        element: HTMLDivElement,
        renderer: CellRenderer
      ) => {
        if (element && !element.shadowRoot) {
          const host =
            element.shadowRoot || element.attachShadow({ mode: "open" });
          const hostElement = host as unknown as Element;

          // Some elements exist conditionally in DOM (don't render if they don't exist).
          render(
            <ThemeProvider theme={defaultTheme}>
              <RMWCProvider>
                <CellState store={store} cellProps={cellProps}>
                  {renderer}
                </CellState>
              </RMWCProvider>
            </ThemeProvider>,
            hostElement
          );
          // Clone link / style nodes into the shadow DOM to make styled-components / material work.
          hostElement.appendChild(materialLink.cloneNode());
          // Deep cloning is necessary on style tag clones.
          hostElement.appendChild(shadowStyle.cloneNode(/* deep */ true));
        }
      };

      const inputPromptElement = cell.getElementsByClassName(
        "jp-InputPrompt"
      )[0] as HTMLDivElement;

      // Override input prompts.
      overrideDOMNode(inputPromptElement, props => (
        // Markdown has no custom play icon.
        <div title={executionTooltip(props.execution, props.promptText)}>
          {props.cellType === "markdown" ? (
            <></>
          ) : props.isDragging ? (
            <Icon icon="drag_indicator" />
          ) : props.promptText === RUNNING_CELL_PROMPT_TEXT ? (
            <CircularProgressButton
              onClick={() => {
                notebookClient.stopExecution();
              }}
              disabled={false}
            />
          ) : props.linesSelected ? (
            <ExecuteSelectionIconButton
              onClick={() => {
                notebookClient.executeActiveSelection();
              }}
              disabled={false}
              title={executionTooltip(props.execution, props.promptText)}
            />
          ) : props.isActive ? (
            <ExecuteIconButton
              onClick={() => {
                notebookClient.executeCell(props.index);
              }}
              disabled={false}
              title={executionTooltip(props.execution, props.promptText)}
            />
          ) : (
            setInnerHtmlSanitized(<div />, inputPromptElement.innerHTML)
          )}
        </div>
      ));

      if (featureFlags.kernelEditorScriptTips) {
        const elem = cell.getElementsByClassName(
          "jp-Cell-inputWrapper"
        )[0] as HTMLDivElement;
        if (elem) {
          const newDiv = document.createElement("div");
          elem.insertAdjacentElement("afterend", newDiv);
          overrideDOMNode(newDiv, props => (
            <ScriptTips scriptTip={props.scriptTip} />
          ));
        }
      }

      // Override cell footers (buttons for adding new cells)
      overrideDOMNode(
        cell.getElementsByClassName("jp-CellFooter")[0] as HTMLDivElement,
        props => (
          <CellSpacer
            location="below"
            index={props.index}
            isActive={props.isActive}
            isHovering={props.isHovering}
            services={services}
            onInsertCell={handlers.onInsertCell}
          />
        )
      );

      cell.setAttribute(
        CELL_BOOTSTRAP_ATTRIBUTE_NAME,
        CellBootstrapStatus.DONE
      );
    });
  };

  // Create client to provide organized way to interact with shadow DOM layer.
  const shadowClient: ShadowClient = {
    scheduleInjectCells: animationFrameDelay(injectCellsShadowDOM),
    scheduleUpdateCells: animationFrameDelay(updateCellsShadowDOM),
  };

  // Callbacks to be passed to JL API signal connects.
  const handleContentChanged = () => {
    shadowClient.scheduleInjectCells();
    shadowClient.scheduleUpdateCells();
  };
  const handleActiveCellChanged = () => shadowClient.scheduleUpdateCells();

  // Setup handlers for future cell change events (add / remove).
  if (jl.context) {
    // Race: If JupyterLab finished extension loading before this script execution, context exists.
    jl.context.model.cells.changed.connect(handleContentChanged);
  } else {
    // Race: Otherwise register callback to run after the extension finishes loading.
    jl.onCreateNew(() => {
      jl.context!.model.cells.changed.connect(handleContentChanged);
    });
  }

  // When changes occur on existing cells, execute the cells changed callback.
  // This triggers state recalculation and pub / sub synchronization to all cell shadows.
  jl.notebookTracker.activeCellChanged.connect(handleActiveCellChanged);
  notebookClient.onSelectionChange(handleActiveCellChanged);

  // When isQueued state changes for a cell, update cells.
  notebookClient.onCellQueueChange(handleActiveCellChanged);

  // statusChanged triggers when cells are executed, this is needed to update
  // run/stop button states.
  jl.context?.sessionContext.statusChanged.connect(handleActiveCellChanged);

  // Execute shadow DOM injection on next animation frame.
  shadowClient.scheduleInjectCells(20);
};
